<script setup>
import { computed, ref, watch } from "vue";
import notification from "@/utils/plugins/NotificationService";
import CompleteMaterialRequestDialog from "./CompleteMaterialRequestDialog.vue";

// eslint-disable-next-line no-undef
const emit = defineEmits(["closeDialog", "saveRequest", "fulfillRequest"]);

// eslint-disable-next-line no-undef
const props = defineProps({
  request: {
    type: Object,
    default: null,
  },
});

const systemsComment = ref("");
const completing = ref(false);
const statusOptions = [
  {
    label: "Pendiente",
    value: "pending",
  },
  {
    label: "Aprobada",
    value: "approved",
  },
  {
    label: "Rechazada",
    value: "rejected",
  },
  {
    label: "Completada",
    value: "fulfilled",
  },
];

const currentStatus = computed(() => props.request?.status || "pending");
const canReview = computed(() => currentStatus.value === "pending");
const canComplete = computed(() => currentStatus.value === "approved");
const completionRequest = computed(() => ({ ...props.request, systemsComment: systemsComment.value }));
const rejectCommentError = ref(false);

const startCompletion = () => {
  if (canComplete.value) completing.value = true;
};

const completeRequest = (payload) => {
  emit("fulfillRequest", { ...payload, systemsComment: systemsComment.value });
};

watch(
  () => props.request,
  (value) => {
    rejectCommentError.value = false;
    systemsComment.value = value?.systemsComment || "";
    completing.value = false;
  },
  { immediate: true }
);

const statusLabel = computed(() => {
  const currentStatus = props.request?.status || "pending";
  const found = statusOptions.find((item) => item.value === currentStatus);
  return found ? found.label : "Pendiente";
});

const closeDialog = () => {
  emit("closeDialog");
};

const saveRequest = (targetStatus = currentStatus.value) => {
  if (completing.value || (targetStatus !== currentStatus.value &&
      !(canReview.value && ["approved", "rejected"].includes(targetStatus)))) return;
  rejectCommentError.value = targetStatus === "rejected" && canReview.value && !systemsComment.value.trim();
  if (rejectCommentError.value) {
    notification.addNotification({ type: "error", message: "Debes introducir un comentario de sistemas.", duration: 5000 });
    return;
  }
  emit("saveRequest", {
    systemsComment: systemsComment.value,
    status: targetStatus,
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const pad = (number) => String(number).padStart(2, "0");

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
</script>

<template>
  <div v-if="completing">
    <CompleteMaterialRequestDialog :request="completionRequest" @close-dialog="completing = false"
      @fulfill-request="completeRequest" />
  </div>
  <div v-else>
    <div class="header">
      <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/box-blue.svg">
      <span class="header-title pl-4">
        Revisar solicitud
      </span>
    </div>

    <div class="d-flex row" v-if="request">
      <div class="col-6 el-col-xs-24 mb-3">
        <label class="w-100">Fecha de solicitud</label>
        <el-input :model-value="formatDateTime(request.requestedAt)" disabled />
      </div>
      <div class="col-6 el-col-xs-24 mb-3">
        <label class="w-100">Empleado</label>
        <el-input :model-value="request.employee?.fullName || ''" disabled />
      </div>
      <div class="col-6 el-col-xs-24 mb-3">
        <label class="w-100">Tipo de material</label>
        <el-input :model-value="request.materialType?.name || ''" disabled />
      </div>
      <div class="col-12 el-col-xs-24 mb-3">
        <label class="w-100">Descripción material</label>
        <el-input :model-value="request.description || ''" type="textarea" rows="4" disabled class="custom-textarea" />
      </div>
      <div class="col-12 el-col-xs-24 mb-3">
        <label class="w-100">Comentario de sistemas</label>
        <el-input v-model="systemsComment" type="textarea" rows="4" class="custom-textarea" />
        <small v-if="rejectCommentError" class="text-danger">Debes introducir un comentario de sistemas</small>
      </div>

      <div class="col-12 mb-3 request-state-field">
        <label id="request-state-label" class="w-100">Estado</label>
        <div class="request-state-row">
          <span class="request-status-badge" :class="'status-' + currentStatus"
            role="status" aria-labelledby="request-state-label">{{ statusLabel }}</span>
          <div v-if="canReview || canComplete" class="request-state-actions"
            :class="{ 'request-state-actions-single': canComplete }">
            <button v-if="canReview" type="button" class="btn request-action-reject edit-button material-request-button"
              @click="saveRequest('rejected')">Rechazar</button>
            <button v-if="canReview" type="button" class="btn request-action-approve edit-button material-request-button"
              @click="saveRequest('approved')">Aprobar</button>
            <button v-if="canComplete" type="button" class="btn request-action-complete edit-button material-request-button"
              @click="startCompletion">Completar</button>
          </div>
        </div>
      </div>
    </div>

    <div class="row footer">
      <div class="col-md-12 mt-4 footer-container">
        <div class="d-flex justify-content-end gap-3">
          <div class="col-md-3">
            <button class="btn btn-block btn_yellow edit-button material-request-button" @click="closeDialog">
              Cerrar
            </button>
          </div>
          <div class="col-md-3">
            <button type="submit" class="btn btn-block btn_red edit-button material-request-button" @click="saveRequest()">
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.header-title {
  font-size: 30px;
  margin-left: 5px;
  font-weight: 800;
  letter-spacing: -0.05em;
  color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
  font-size: 14px !important;
  border-radius: 20px;
}

.footer-container {
  margin-left: 0;
}

.request-state-row,
.request-state-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.request-state-field > label {
  display: block;
  margin-bottom: 0.5rem;
}

.request-state-row {
  justify-content: flex-start;
}

.request-state-actions {
  margin-left: auto;
  justify-content: flex-end;
}

.request-state-actions .edit-button {
  width: auto;
  min-width: 112px;
  min-height: 44px;
  margin-bottom: 0;
}

.request-state-actions .edit-button {
  color: #fff;
}

.request-action-reject { background-color: #ff0000; }
.request-action-approve { background-color: #27ae60; }
.request-action-complete { background-color: #107dde; }

.request-state-actions .edit-button:hover,
.request-state-actions .edit-button:active {
  color: #fff;
  filter: brightness(0.9);
}

.request-state-actions .edit-button:focus-visible {
  color: #fff;
  outline: 2px solid #409eff;
  outline-offset: 2px;
}

.request-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  height: 34px;
  line-height: 1;
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  text-align: center;
  text-transform: uppercase;
}

.status-pending { background-color: #f1c40f; }
.status-approved { background-color: #27ae60; }
.status-rejected { background-color: #e53935; }
.status-fulfilled,
.status-fullfilled { background-color: #107dde; }

@media (max-width: 767.98px) {
  .request-status-badge {
    height: 44px;
  }


  .footer-container {
    margin-left: 0 !important;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  [class*="col-"] {
    padding-left: 0;
    padding-right: 0;
  }

  .footer {
    .d-flex {
      flex-direction: column;
      gap: 0.75rem !important;
    }

    .col-md-3,
    .col-md-4 {
      width: 100%;
    }

    .edit-button {
      width: 100%;
    }
  }
}

@media (max-width: 500px) {
  .header-title {
    font-size: 25px;
  }
}
@import "./materialRequestButtons";
</style>
